import type { Response } from 'lib-shared-legacy-types';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import type { ApiResponse, Queue } from 'lib-shared-types-api';
import type { Action } from 'redux-actions';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { ajaxSend } from 'api/api';
import type { PhoneNumber } from 'apis/numbers';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { actionDiff } from 'components/powerFlowShared/helpers/actionDiff';
import { pubSub } from 'utils/pubSub';

import { notificationActions } from '../../notifications/actions/notification';
import { getRequestID } from '../../shared/util/request';
import {
  addFlow as addFlowAPI,
  getFlow as getFlowAPI,
  updateFlow as updateFlowAPI,
} from '../api/callFlow';
import type {
  FlowCallActionT,
  FlowCallCreateFlowT,
  FlowCallFlowT,
} from '../api/types';
import {
  FLOW_ADD_AFTER_BUY_NUMBER,
  FLOW_ADD_AFTER_BUY_NUMBER_FAILED,
  FLOW_ADD_AFTER_BUY_NUMBER_SUCCEEDED,
  FLOW_FETCH,
  FLOW_FETCH_FAILED,
  FLOW_FETCH_SUCCEEDED,
  FLOW_UPDATE,
  FLOW_UPDATE_FAILED,
  FLOW_UPDATE_SUCCEEDED,
} from '../constants/actionTypes';

type FetchFlowActionPayloadT = {
  id: string;
};

type UpdateFlowPayloadT = {
  flow: FlowCallFlowT;
  initialActions: FlowCallActionT[];
};

type BuildTempFlowPayloadT = {
  result: Response<PhoneNumber>;
};

const { notificationAdd, notificationRemoveById } = notificationActions;

function getQueue(queueId: string): Promise<ApiResponse<Queue>> {
  return ajaxSend({
    path: `/v2/queues/${queueId}`,
  });
}

function* fetchFlow(action: Action<FetchFlowActionPayloadT>) {
  try {
    const data = yield call(getFlowAPI, action.payload.id);
    yield put({ type: FLOW_FETCH_SUCCEEDED, flow: data });
  } catch (e: any) {
    yield put({ type: FLOW_FETCH_FAILED, message: e.message });
  }
}

function* updateFlow(action: Action<UpdateFlowPayloadT>) {
  const { payload } = action;
  const { flow, initialActions } = payload;
  const { name } = flow;

  try {
    const result = yield call(updateFlowAPI, flow);
    yield put({ type: FLOW_UPDATE_SUCCEEDED, flow: result.data });
    yield put(
      notificationAdd({
        title: 'Saved',
        text: `"${name}" is saved.`,
        icon: 'i-done',
        className: 'success',
        time: 3000,
        showDesktop: false,
      }),
    );
  } catch (e: any) {
    yield put(
      notificationAdd({
        className: 'warning',
        icon: 'i-exclamation',
        showDesktop: false,
        text: 'An error occurred when trying to save the call flow',
        title: 'Could not save',
      }),
    );
    Sentry.captureException(e, {
      contexts: {
        response: e?.response?.data?.data,
        flow: {
          data: flow,
          diff: actionDiff(flow.actions, initialActions),
        },
      },
      tags: { team: SentryTeam.Routing },
    });
    yield put({ type: FLOW_UPDATE_FAILED, message: e.message });
  }
}

function* buildTempFlow(action: Action<BuildTempFlowPayloadT>) {
  try {
    let defaultQueue: null | Queue = null;
    let defaultQueueLanguage = 'en-GB';
    const phoneNumber = action.payload.result.data;
    const initNodeId = getRequestID();
    // Get settings (we need the default queue id).
    const getSettings = (state) => state.settings;
    const settingsState = yield select(getSettings);

    // Get default queue by id.
    if (settingsState.organization.organization.default_queue) {
      const queueResult = yield call(
        getQueue,
        settingsState.organization.organization.default_queue,
      );

      if (queueResult?.data) {
        defaultQueue = queueResult.data;
      }

      if (phoneNumber.e164_format.indexOf('+45') > -1) {
        defaultQueueLanguage = 'da-DK';
      }
    }

    // Build the default flow with the default queue and phoneNumber.
    if (defaultQueue) {
      const defaultFlow: FlowCallCreateFlowT = {
        actions: [
          {
            badges: [],
            childIds: [],
            connectionLabel: 'arrow',
            icon: null,
            id: initNodeId,
            isAction: false,
            isEditable: false,
            isRemovable: false,
            nodeType: FlowNodeTypes.INIT,
            overlay: [],
            pageX: 1800,
            pageY: 700,
            parentId: null,
            payload: null,
            text: 'Incoming Call',
          },
          {
            badges: [],
            childIds: [],
            connectionLabel: 'arrow',
            icon: 'users',
            id: getRequestID(),
            isAction: false,
            isEditable: true,
            isRemovable: true,
            nodeType: FlowNodeTypes.QUEUE,
            overlay: [],
            pageX: 1800,
            pageY: 820,
            payload: {
              queue: {
                label: defaultQueue.name,
                value: defaultQueue.id,
              },
              language: defaultQueueLanguage,
            },
            parentId: initNodeId,
            text: defaultQueue.name,
          },
        ],
        channel: 'pstn_phone',
        active: true,
        location: phoneNumber.e164_format,
        name: `Default call flow for ${phoneNumber.e164_format}`,
      };

      const result = yield call(addFlowAPI, defaultFlow);

      yield put({
        type: FLOW_ADD_AFTER_BUY_NUMBER_SUCCEEDED,
        flow: result.data,
      });

      // Add notification after call flow has been added with goto call flow.
      const notificationID = getRequestID();
      const notificationOptions = {
        id: notificationID,
        title: 'New call flow added',
        text: `We have created a call flow for ${phoneNumber.e164_format} - you can now call it.`,
        className: 'success',
        type: 'callFlowAdded',
        icon: 'i-done-double',
        time: 10000,
        leftAction: {
          text: 'Dismiss',
          action: function* () {
            yield put(notificationRemoveById({ id: notificationID }));
          },
        },
      };

      yield put(notificationAdd(notificationOptions));

      pubSub.publish('navigate', {
        path: '/settings/phone/flow/:flowId',
        params: {
          path: { flowId: `${result.data.id}` },
        },
      });
    }
  } catch (e: any) {
    console.warn('buildTempFlow exception', e);
    yield put({ type: FLOW_ADD_AFTER_BUY_NUMBER_FAILED, message: e.message });
  }
}

export const callFlow = [
  takeLatest(FLOW_FETCH, fetchFlow),
  takeEvery(FLOW_UPDATE, updateFlow),
  takeEvery(FLOW_ADD_AFTER_BUY_NUMBER, buildTempFlow),
];
